import { useAuth } from '../auth/authProvider';

export const usePermissions = () => {
  const { hasPermission, hasRole, permissions, roles } = useAuth();

  return {
    // Check single permission
    can: (resource: string, action: string) => hasPermission(resource, action),
    
    // Check multiple permissions (ALL required)
    canAll: (checks: Array<{ resource: string; action: string }>) => 
      checks.every(check => hasPermission(check.resource, check.action)),
    
    // Check multiple permissions (ANY required)
    canAny: (checks: Array<{ resource: string; action: string }>) => 
      checks.some(check => hasPermission(check.resource, check.action)),
    
    // Role checks
    hasRole,
    hasAnyRole: (roleNames: string[]) => roleNames.some(role => hasRole(role)),
    hasAllRoles: (roleNames: string[]) => roleNames.every(role => hasRole(role)),
    
    // Raw data
    permissions,
    roles,
    
    // Common permission checks
    canViewUsers: () => hasPermission('users', 'read'),
    canCreateUsers: () => hasPermission('users', 'create'),
    canUpdateUsers: () => hasPermission('users', 'update'),
    canDeleteUsers: () => hasPermission('users', 'delete'),
    
    canViewLicenses: () => hasPermission('licenses', 'read'),
    canCreateLicenses: () => hasPermission('licenses', 'create'),
    canUpdateLicenses: () => hasPermission('licenses', 'update'),
    canDeleteLicenses: () => hasPermission('licenses', 'delete'),
    
    canViewCustomers: () => hasPermission('customers', 'read'),
    canCreateCustomers: () => hasPermission('customers', 'create'),
    canUpdateCustomers: () => hasPermission('customers', 'update'),
    canDeleteCustomers: () => hasPermission('customers', 'delete'),
    
    canViewAudit: () => hasPermission('audit', 'read'),
    canExportAudit: () => hasPermission('audit', 'export'),
    
    canViewAnalytics: () => hasPermission('analytics', 'read'),
    canExportAnalytics: () => hasPermission('analytics', 'export'),
    
    // Release permissions (uses 'updates' resource in database)
    canViewReleases: () => hasPermission('updates', 'read'),
    canCreateReleases: () => hasPermission('updates', 'create'),
    canUpdateReleases: () => hasPermission('updates', 'update'),
    canDeleteReleases: () => hasPermission('updates', 'delete'),
    canManageRollouts: () => hasPermission('updates', 'rollout'),
    
    // API Key permissions
    canViewApiKeys: () => hasPermission('api_keys', 'read'),
    canCreateApiKeys: () => hasPermission('api_keys', 'create'),
    canUpdateApiKeys: () => hasPermission('api_keys', 'update'),
    canDeleteApiKeys: () => hasPermission('api_keys', 'delete'),

    // Customer Users (Portal) permissions
    canViewCustomerUsers: () => hasPermission('customer_users', 'read'),
    canCreateCustomerUsers: () => hasPermission('customer_users', 'create'),
    canUpdateCustomerUsers: () => hasPermission('customer_users', 'update'),
    canDeleteCustomerUsers: () => hasPermission('customer_users', 'delete'),
  };
};